import { useCallback, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { Text } from '@antimatterhq/ui/components/typography';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Label } from '@antimatterhq/ui/components/label';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { CustomNotebookFunction } from '../customNotebookFunction/CustomNotebookFunction';
import { JSONExtraction } from '../jsonExtract/JSONExtract';
import { UnreferencedFields } from '../unreferencedFields/UnreferencedFields';
import { DataSourceFilters } from '../filters/DataSourceFilters';
import { DatasourceTemporaryFields } from '../temporaryFields/DatasourceTemporaryFields';
import { FieldSpecTable } from '@/components/table/FieldSpecTable/FieldSpecTable';
import {
  addFieldSchemaValue,
  deleteFieldSchemaValue,
  generateNotebookFunctionName,
  generateNotebookName,
  getCopyableFields,
  getFieldTableRows,
  getTakenNames,
  setCustomFunctionRequest,
  setFieldSchemaValues,
  updateFieldSchemaValue,
} from '@/lib/datasource';
import CustomFieldDialog from '@/components/dialog/customField/CustomField';
import {
  AddTableSchema,
  DataSourceTablesSchemaValues,
  FieldSpecSchemaValues,
} from '@/zod/schemas/dataSource/Common';
import { FormDatabricksPathAndTableValidatedInput } from '@/components/form/input/FormDatabricksPathAndTableValidatedInput';
import { FormDropdown, FormError } from '@/components/form/form';
import { FormValues } from '@/components/form/values/FormValues';
import { useCreateNotebookFunction } from '@/hooks/dbui/useCreateNotebookFunction';
import { getFormikNames } from '@/utils/util';
import { useConfigDataSource } from '@/contexts/ConfigDataSourceContext';
import { optCustomTooltip, optDefaultTooltip } from '@/strings/StringsTooltips';
import { useCreateNotebook } from '@/hooks/dbui/useCreateNotebook';
import {
  FormAccordion,
  FormAccordionContent,
  FormAccordionItem,
  FormAccordionTrigger,
} from '@/components/form/accordion/FormAccordion';
import { Datasource_FieldSpec } from '@/types/api';

export type AddTableProps = {
  index: number;
  currentTable: 'gold' | 'silver';
  previousTable: 'silver' | 'bronze';
  table: NonNullable<DataSourceTablesSchemaValues['addTables']>[number];
  sourceTableNames?: string[];
  enableClustering?: boolean;
};

type AddTableKeys = keyof typeof AddTableSchema.shape;

export const AddTable = ({
  index,
  currentTable,
  previousTable,
  table,
  sourceTableNames,
  enableClustering = false,
}: AddTableProps) => {
  const formik = useFormikContext<DataSourceTablesSchemaValues>();
  const { dataSource } = useConfigDataSource();
  const [customField, setCustomField] = useState<
    FieldSpecSchemaValues | undefined
  >(undefined);
  const { enableJsonExtraction, enableTemporaryFields } = useFlags<FlagSet>();

  const {
    mutateAsync: createCustomNotebook,
    isPending: isCreatingCustomNotebook,
  } = useCreateNotebook();
  const {
    mutateAsync: createNotebookFunction,
    isPending: isCreatingNotebookFunction,
  } = useCreateNotebookFunction();

  const { setFieldValue, handleChange, handleBlur, isSubmitting } = formik;
  const prefix = `addTables.${index}.`;

  const names = useMemo(
    () => getFormikNames<AddTableKeys>(AddTableSchema.shape, prefix),
    [prefix],
  );

  const fieldSpecData = useMemo(() => {
    return getFieldTableRows([], table.fields);
  }, [table]);

  const onDeleteField = useCallback(
    (field: Datasource_FieldSpec) => {
      setFieldValue(
        `${prefix}fields`,
        deleteFieldSchemaValue(field.name!, table.fields),
        true,
      );
    },
    [table.fields, setFieldValue, prefix],
  );

  const takenNames = useMemo(
    () => getTakenNames(fieldSpecData),
    [fieldSpecData],
  );

  const copyableFields = useMemo(
    () => getCopyableFields(fieldSpecData),
    [fieldSpecData],
  );

  const addField = useCallback(
    (fieldSpec: Datasource_FieldSpec) => {
      setFieldValue(
        `${prefix}fields`,
        addFieldSchemaValue(fieldSpec, table.fields),
        true,
      );
    },
    [setFieldValue, table, prefix],
  );

  const updateField = useCallback(
    (fieldSpec: Datasource_FieldSpec) => {
      setFieldValue(
        `${prefix}fields`,
        updateFieldSchemaValue(fieldSpec, customField, table.fields),
        true,
      );
    },
    [table, setFieldValue, prefix, customField],
  );

  const onEditField = useCallback(
    ({
      custom,
    }: {
      preset?: Datasource_FieldSpec;
      custom?: FieldSpecSchemaValues;
    }) => {
      setCustomField(custom);
    },
    [],
  );

  const onCreateCustomNotebookFunction = useCallback(async () => {
    let customNotebookPath = formik.values.customNotebookPath;
    if (!customNotebookPath) {
      const path = generateNotebookName(dataSource.metadata?.name || '');
      await createCustomNotebook(
        { path, templateType: 'datasource' },
        {
          onSuccess: async ({ data }) => {
            customNotebookPath = data.notebookPath;
            setFieldValue('customNotebookPath', customNotebookPath);
          },
          toasts: {
            success: 'Notebook created!',
            error: 'Failed to create notebook',
          },
        },
      );
    }
    const name = generateNotebookFunctionName(
      dataSource.metadata?.name || '',
      currentTable,
    );
    const request = setCustomFunctionRequest(
      customNotebookPath || '',
      name,
      table,
      setFieldSchemaValues(table.fields),
    );
    createNotebookFunction(request, {
      onSuccess: async ({ data }) => {
        setFieldValue(names.customFunction, name);
        setFieldValue(names.tableMode, 'custom');
        window.open(data.notebookUrl, '_blank', 'noopener,noreferrer');
      },
      toasts: {
        success: 'Notebook function created',
        error: 'Failed to create notebook function',
      },
    });
  }, [
    createNotebookFunction,
    dataSource,
    table,
    setFieldValue,
    names,
    currentTable,
    createCustomNotebook,
    formik.values.customNotebookPath,
  ]);

  const disabled = isSubmitting || table.tableMode === 'disable';
  const enableUtils = currentTable === 'silver';

  return (
    <>
      <Spacer size="lg" />

      <Label htmlFor={`${prefix}name`} id={`${prefix}name-label`} required>
        Table name
      </Label>

      <FormDatabricksPathAndTableValidatedInput
        name={`${prefix}name`}
        id={`${prefix}name`}
        disabled={isSubmitting}
        placeholder="Table name"
        constraint="mustExistReadWriteOrBeCreatable"
        validationType="table"
        tableSchema={currentTable === 'silver' ? 'silverSchema' : 'goldSchema'}
        required
      />
      <FormError name={`${prefix}name`} />

      <Spacer size="lg" />
      <ButtonGroup
        data-testid={`add-table-mode-${index}-button-group`}
        options={[
          { label: 'Configure preset & fields', value: 'configure' },
          { label: 'Custom function', value: 'custom' },
          { label: 'Disable table', value: 'disable' },
        ]}
        value={table.tableMode}
        onChange={(value) => {
          setFieldValue(names.tableMode, value);
        }}
        disabled={isSubmitting}
        id={`add-table-mode-${index}`}
      />
      {!!sourceTableNames && (
        <>
          <Spacer size="lg" />

          <Label
            htmlFor={`${prefix}sourceTable`}
            id={`${prefix}sourceTable-label`}
            required
          >
            Source table
          </Label>
          <FormDropdown
            placeholder="Source table"
            options={sourceTableNames}
            value={table.sourceTable}
            onChange={handleChange}
            onBlur={handleBlur}
            name={`${prefix}sourceTable`}
            disabled={disabled}
            aria-labelledby={`${prefix}sourceTable-label`}
          />
          <FormError name={`${prefix}sourceTable`} />
        </>
      )}
      {table.tableMode === 'custom' && (
        <>
          <Spacer size="lg" />
          <CustomNotebookFunction
            values={table}
            prefix={prefix}
            currentTable={currentTable}
            notebookPath={formik.values.customNotebookPath}
          />
        </>
      )}

      {table.tableMode !== 'custom' && (
        <>
          <Spacer size="lg" />

          <Text size="lg" className="block" bold>
            Current field list
          </Text>
          <FieldSpecTable
            data={fieldSpecData}
            onDelete={onDeleteField}
            onEdit={onEditField}
            disabled={disabled}
            data-testid={`add-table-field-spec-table-${index}`}
          />
          <FormError name={`${prefix}fields`} />
          <div>
            <Spacer size="lg" />
            <Button
              leftIcon="radix-icons:plus"
              variant="default"
              className="w-fit"
              onClick={() => setCustomField({})}
              type="button"
              disabled={disabled}
              data-testid={`add-table-add-field-button-${index}`}
            >
              Add a Field
            </Button>
          </div>

          {enableClustering && (
            <>
              <Spacer size="xl" />

              <ButtonGroup
                options={[
                  {
                    label: 'Default clustering',
                    value: 'default',
                    info: { content: optDefaultTooltip },
                  },
                  {
                    label: 'Custom clustering',
                    value: 'custom',
                    info: { content: optCustomTooltip },
                  },
                ]}
                value={table.enableOverrideLiquidColumns ? 'custom' : 'default'}
                onChange={(value) => {
                  formik.setFieldValue(
                    `${prefix}enableOverrideLiquidColumns`,
                    value === 'custom',
                  );
                }}
                id={`add-table-clustering-${index}`}
                data-testid={`add-table-clustering-${index}-button-group`}
              />

              {table.enableOverrideLiquidColumns && (
                <>
                  <Spacer size="lg" />

                  <Label
                    htmlFor={`${prefix}overrideLiquidColumns`}
                    id={`${prefix}overrideLiquidColumns-label`}
                  >
                    Liquid clustering columns
                  </Label>

                  <FormValues
                    values={table.overrideLiquidColumns}
                    onChange={handleChange}
                    name={`${prefix}overrideLiquidColumns`}
                    onBlur={handleBlur}
                    aria-labelledby={`${prefix}overrideLiquidColumns-label`}
                    data-testid={`add-table-clustering-${index}-values`}
                  />
                </>
              )}
            </>
          )}

          <Spacer size="base" />

          <FormAccordion type="multiple">
            <FormAccordionItem value="filters">
              <FormAccordionTrigger
                data-testid={`add-table-filters-accordion-${index}-filters`}
              >
                <Text size="lg">Filters</Text>
              </FormAccordionTrigger>
              <FormAccordionContent>
                <DataSourceFilters filters={table} prefix={prefix} />
              </FormAccordionContent>
            </FormAccordionItem>

            {enableUtils && (
              <FormAccordionItem value="preserve">
                <FormAccordionTrigger
                  data-testid={`add-table-preserve-accordion-${index}-preserve`}
                >
                  <Text size="lg">Preserve unreferenced fields</Text>
                </FormAccordionTrigger>
                <FormAccordionContent className="-m-2 p-2">
                  <UnreferencedFields
                    unreferencedFields={table}
                    prefix={prefix}
                  />
                </FormAccordionContent>
              </FormAccordionItem>
            )}

            {enableJsonExtraction && enableUtils && (
              <FormAccordionItem value="json">
                <FormAccordionTrigger
                  data-testid={`add-table-json-accordion-${index}-json`}
                >
                  <Text size="lg">JSON extraction</Text>
                </FormAccordionTrigger>
                <FormAccordionContent className="-m-2 p-2">
                  <JSONExtraction
                    jsonExtract={table.jsonExtract}
                    prefix={prefix}
                  />
                </FormAccordionContent>
              </FormAccordionItem>
            )}

            {enableTemporaryFields && enableUtils && (
              <FormAccordionItem value="temporaryFields">
                <FormAccordionTrigger
                  data-testid={`add-table-temporary-fields-accordion-${index}-temporaryFields`}
                >
                  <Text size="lg">Temporary fields</Text>
                </FormAccordionTrigger>
                <FormAccordionContent className="-m-2 p-2">
                  <DatasourceTemporaryFields
                    values={table}
                    prefix={prefix}
                    disabled={disabled}
                    currentTable={currentTable}
                    previousTable={previousTable}
                  />
                </FormAccordionContent>
              </FormAccordionItem>
            )}
          </FormAccordion>

          <Spacer size="xl" />

          <Button
            variant="default"
            onClick={() => onCreateCustomNotebookFunction()}
            disabled={
              disabled || isCreatingNotebookFunction || isCreatingCustomNotebook
            }
            leftIcon="lucide:notebook"
            loading={isCreatingNotebookFunction || isCreatingCustomNotebook}
            data-testid={`add-table-create-custom-function-button-${index}`}
          >
            Convert to custom function
          </Button>
        </>
      )}

      {customField && (
        <CustomFieldDialog
          currentTable={currentTable}
          currentTableFields={copyableFields.filter(
            (name) => name !== customField.name,
          )}
          previousTable={previousTable}
          onSubmit={(fieldSpec: Datasource_FieldSpec) => {
            if (Object.keys(customField).length === 0) {
              addField(fieldSpec);
            } else {
              updateField(fieldSpec);
            }
          }}
          fieldSpec={customField}
          open={true}
          onClose={() => setCustomField(undefined)}
          takenNames={takenNames.filter((name) => name !== customField.name)}
        />
      )}
    </>
  );
};
